import React, { useState, useEffect } from 'react';
import { CheckCircle, Phone, Mail, Send, Shield, TrendingUp, Users, ArrowRight, RefreshCw } from 'lucide-react';
import { Helmet } from 'react-helmet-async';
import { toast } from 'sonner';
import { companyInfo } from '../mock/data';

// Web3Forms Access Key
const WEB3FORMS_ACCESS_KEY = '153572c2-262d-4749-8ba6-a2d6aaa74108';

// Generate random captcha
const generateCaptcha = () => {
  const num1 = Math.floor(Math.random() * 10) + 1;
  const num2 = Math.floor(Math.random() * 10) + 1;
  return { num1, num2, answer: num1 + num2 };
};

const LandingPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    mobile: '',
    consent: false
  });
  const [captcha, setCaptcha] = useState(generateCaptcha());
  const [captchaInput, setCaptchaInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  // Refresh captcha
  const refreshCaptcha = () => {
    setCaptcha(generateCaptcha());
    setCaptchaInput('');
  };

  const services = [
    {
      name: 'EQUI RIDER',
      price: '₹1,500',
      features: [
        '2–3 swing trade ideas per week',
        'Clear entry, stop-loss & targets',
        'Weekly market commentary',
        'Telegram & WhatsApp alerts'
      ],
      popular: false
    },
    {
      name: 'EQUI HUNTER',
      price: '₹6,666',
      features: [
        '2–3 Cash Calls daily',
        '2–3 Derivatives Calls daily',
        'Monthly portfolio review',
        'Daily & weekly market reports'
      ],
      popular: true
    },
    {
      name: 'EQUI MASTER',
      price: '₹8,333',
      features: [
        'All EQUI HUNTER features',
        'Commodity calls (Gold, Silver, Crude)',
        'Long-term investment themes',
        'Priority webinar access'
      ],
      popular: false
    }
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate captcha
    if (parseInt(captchaInput) !== captcha.answer) {
      toast.error('Incorrect answer. Please solve the math problem.');
      refreshCaptcha();
      return;
    }
    
    if (!formData.consent) {
      toast.error('Please provide consent to proceed.');
      return;
    }

    setLoading(true);
    
    try {
      // Build submission data
      const submissionData = {
        access_key: WEB3FORMS_ACCESS_KEY,
        subject: `🎯 New Lead from Landing Page: ${formData.name}`,
        from_name: 'Equi Pulse Landing Page',
        name: formData.name,
        mobile: formData.mobile,
        consent: 'Yes - Agreed to receive communications',
        source: 'Social Media Landing Page'
      };

      const response = await fetch('https://api.web3forms.com/submit', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(submissionData)
      });
      
      const result = await response.json();
      
      if (result.success) {
        setSubmitted(true);
        toast.success('Thank you! Our team will contact you shortly.');
      } else {
        // Show actual error from Web3Forms if available
        const errorMsg = result.message || 'Something went wrong. Please try again.';
        console.error('Web3Forms error:', result);
        toast.error(errorMsg);
      }
    } catch (error) {
      console.error('Form error:', error);
      toast.error('Network error. Please check your connection and try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-emerald-950/30 to-slate-950">
      {/* SEO Meta Tags */}
      <Helmet>
        <title>Free Consultation | Start Investing Today | Equi Pulse</title>
        <meta name="description" content="Get a free consultation from SEBI-registered analysts at Equi Pulse. Start your investment journey with expert guidance on stocks and trading." />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href="https://www.equipulseresearch.com/promo" />
        <meta property="og:title" content="Free Consultation | Start Investing Today | Equi Pulse" />
        <meta property="og:description" content="Get a free consultation from SEBI-registered analysts at Equi Pulse. Start your investment journey with expert guidance." />
        <meta property="og:url" content="https://www.equipulseresearch.com/promo" />
        <meta property="og:image" content="https://www.equipulseresearch.com/og-image.png" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Free Consultation | Start Investing Today | Equi Pulse" />
        <meta name="twitter:description" content="Get a free consultation from SEBI-registered analysts at Equi Pulse." />
      </Helmet>
      
      {/* Header */}
      <header className="bg-slate-950/90 backdrop-blur-md border-b border-amber-500/20 sticky top-0 z-50" role="banner">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <picture>
                <source srcSet="/logo.webp" type="image/webp" />
                <img 
                  src="/logo.png" 
                  alt="Equi Pulse Research Logo - SEBI Registered Research Analyst"
                  width="40"
                  height="40"
                  className="h-10 w-auto"
                />
              </picture>
              <div>
                <span className="text-xl font-bold text-amber-400">Equi Pulse</span>
                <p className="text-xs text-gray-400">SEBI Reg: {companyInfo.sebiReg}</p>
              </div>
            </div>
            <a
              href={`tel:${companyInfo.phone}`}
              className="flex items-center gap-2 px-4 py-2 bg-emerald-700/20 border border-emerald-500/30 rounded-lg text-emerald-400 hover:bg-emerald-700/30 transition-all duration-200"
            >
              <Phone className="w-4 h-4" aria-hidden="true" />
              <span className="hidden sm:inline text-sm font-medium">Call Now</span>
              <span className="sm:hidden sr-only">Call us</span>
            </a>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="container mx-auto px-4 py-8 lg:py-16">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-16 items-start">
          
          {/* Left Side - Contact Form (Appears first on mobile) */}
          <div className="order-1 lg:order-1">
            <div className="bg-gradient-to-br from-slate-900 to-slate-800 rounded-2xl border border-amber-500/20 shadow-2xl shadow-emerald-900/20 overflow-hidden">
              {/* Form Header */}
              <div className="bg-gradient-to-r from-emerald-800 to-emerald-700 px-6 py-5">
                <h2 className="text-2xl font-bold text-white">Start Your Investment Journey</h2>
                <p className="text-emerald-100/80 text-sm mt-1">Get expert guidance from SEBI registered analysts</p>
              </div>

              {/* Form Body */}
              <div className="p-6">
                {!submitted ? (
                  <form onSubmit={handleSubmit} className="space-y-5">
                    {/* Honeypot */}
                    <input type="checkbox" name="botcheck" className="hidden" style={{ display: 'none' }} />
                    
                    {/* Name Field */}
                    <div>
                      <label htmlFor="name" className="block text-sm font-medium text-gray-300 mb-2">
                        Full Name <span className="text-red-400">*</span>
                      </label>
                      <input
                        type="text"
                        id="name"
                        data-testid="landing-name-input"
                        required
                        aria-required="true"
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        className="w-full px-4 py-3 bg-slate-800/50 border border-emerald-700/30 rounded-lg text-white placeholder-gray-400 focus:border-amber-500 focus:outline-none focus:ring-2 focus:ring-amber-500/20 transition-all duration-200"
                        placeholder="Enter your full name"
                      />
                    </div>

                    {/* Mobile Field */}
                    <div>
                      <label htmlFor="mobile" className="block text-sm font-medium text-gray-300 mb-2">
                        Mobile Number <span className="text-red-400">*</span>
                      </label>
                      <div className="relative">
                        <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 text-sm">+91</span>
                        <input
                          type="tel"
                          id="mobile"
                          data-testid="landing-mobile-input"
                          required
                          aria-required="true"
                          pattern="[0-9]{10}"
                          maxLength="10"
                          value={formData.mobile}
                          onChange={(e) => setFormData({ ...formData, mobile: e.target.value.replace(/\D/g, '') })}
                          className="w-full pl-14 pr-4 py-3 bg-slate-800/50 border border-emerald-700/30 rounded-lg text-white placeholder-gray-400 focus:border-amber-500 focus:outline-none focus:ring-2 focus:ring-amber-500/20 transition-all duration-200"
                          placeholder="9876543210"
                        />
                      </div>
                    </div>

                    {/* Simple Math Captcha */}
                    <div>
                      <label htmlFor="captcha" className="block text-sm font-medium text-gray-300 mb-2">
                        Verify You&apos;re Human <span className="text-red-400">*</span>
                      </label>
                      <div className="flex items-center gap-3">
                        <div className="flex items-center gap-2 px-4 py-3 bg-emerald-900/30 border border-emerald-700/50 rounded-lg">
                          <span className="text-amber-400 font-bold text-lg">{captcha.num1}</span>
                          <span className="text-white font-bold">+</span>
                          <span className="text-amber-400 font-bold text-lg">{captcha.num2}</span>
                          <span className="text-white font-bold">=</span>
                        </div>
                        <input
                          type="number"
                          id="captcha"
                          data-testid="landing-captcha-input"
                          required
                          aria-required="true"
                          value={captchaInput}
                          onChange={(e) => setCaptchaInput(e.target.value)}
                          className="w-20 px-4 py-3 bg-slate-800/50 border border-emerald-700/30 rounded-lg text-white text-center font-bold focus:border-amber-500 focus:outline-none focus:ring-2 focus:ring-amber-500/20 transition-all duration-200"
                          placeholder="?"
                        />
                        <button
                          type="button"
                          onClick={refreshCaptcha}
                          className="p-3 bg-slate-700 hover:bg-slate-600 rounded-lg transition-colors"
                          aria-label="Generate new captcha question"
                        >
                          <RefreshCw className="w-5 h-5 text-gray-300" aria-hidden="true" />
                        </button>
                      </div>
                    </div>

                    {/* Consent Checkbox */}
                    <div className="pt-2">
                      <label className="flex items-start gap-3 cursor-pointer group">
                        <div className="relative flex-shrink-0 mt-0.5">
                          <input
                            type="checkbox"
                            data-testid="landing-consent-checkbox"
                            required
                            checked={formData.consent}
                            onChange={(e) => setFormData({ ...formData, consent: e.target.checked })}
                            className="sr-only peer"
                          />
                          <div className="w-5 h-5 border-2 border-emerald-600 rounded bg-slate-800/50 peer-checked:bg-emerald-600 peer-checked:border-emerald-600 transition-all duration-200 flex items-center justify-center">
                            {formData.consent && (
                              <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                                <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                              </svg>
                            )}
                          </div>
                        </div>
                        <span className="text-sm text-gray-400 group-hover:text-gray-300 transition-colors">
                          I consent to share my information and agree to receive communications from Equi Pulse Research regarding investment advisory services. <span className="text-red-400">*</span>
                        </span>
                      </label>
                    </div>

                    {/* Submit Button */}
                    <button
                      type="submit"
                      data-testid="landing-submit-button"
                      disabled={loading}
                      aria-busy={loading}
                      className="w-full px-6 py-4 bg-gradient-to-r from-amber-600 to-amber-500 hover:from-amber-500 hover:to-amber-400 disabled:from-gray-600 disabled:to-gray-600 text-white font-bold text-lg rounded-lg shadow-lg shadow-amber-900/30 transition-all duration-300 flex items-center justify-center gap-2 group"
                    >
                      {loading ? (
                        <>
                          <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                          </svg>
                          <span>Submitting...</span>
                        </>
                      ) : (
                        <>
                          <span>Get Free Consultation</span>
                          <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" aria-hidden="true" />
                        </>
                      )}
                    </button>

                    {/* Trust Badges */}
                    <div className="flex items-center justify-center gap-6 pt-4 border-t border-emerald-700/20">
                      <div className="flex items-center gap-1.5 text-gray-400 text-xs">
                        <Shield className="w-4 h-4 text-emerald-500" aria-hidden="true" />
                        <span>SEBI Registered</span>
                      </div>
                      <div className="flex items-center gap-1.5 text-gray-400 text-xs">
                        <Users className="w-4 h-4 text-emerald-500" aria-hidden="true" />
                        <span>500+ Clients</span>
                      </div>
                    </div>
                  </form>
                ) : (
                  /* Success State */
                  <div className="text-center py-8">
                    <div className="w-20 h-20 mx-auto mb-6 bg-emerald-600/20 rounded-full flex items-center justify-center">
                      <CheckCircle className="w-10 h-10 text-emerald-500" aria-hidden="true" />
                    </div>
                    <h3 className="text-2xl font-bold text-white mb-2">Thank You!</h3>
                    <p className="text-gray-400 mb-6">
                      Our expert team will contact you within 24 hours to discuss your investment goals.
                    </p>
                    <div className="flex flex-col sm:flex-row gap-3 justify-center">
                      <a
                        href={`https://wa.me/${companyInfo.whatsapp}?text=Hi%20Equi%20Pulse!%20I%20just%20submitted%20a%20form%20and%20would%20like%20to%20know%20more.`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center justify-center gap-2 px-5 py-2.5 bg-emerald-600 hover:bg-emerald-500 text-white font-medium rounded-lg transition-all duration-200"
                      >
                        <Send className="w-4 h-4" aria-hidden="true" />
                        Chat on WhatsApp
                      </a>
                      <a
                        href={`tel:${companyInfo.phone}`}
                        className="inline-flex items-center justify-center gap-2 px-5 py-2.5 bg-slate-700 hover:bg-slate-600 text-white font-medium rounded-lg transition-all duration-200"
                      >
                        <Phone className="w-4 h-4" aria-hidden="true" />
                        Call Now
                      </a>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Quick Contact */}
            <div className="mt-6 flex flex-wrap items-center justify-center gap-4 text-sm">
              <a href={`tel:${companyInfo.phone}`} className="flex items-center gap-2 text-gray-400 hover:text-amber-400 transition-colors">
                <Phone className="w-4 h-4" aria-hidden="true" />
                {companyInfo.phone}
              </a>
              <span className="text-gray-600" aria-hidden="true">|</span>
              <a href={`mailto:${companyInfo.email}`} className="flex items-center gap-2 text-gray-400 hover:text-amber-400 transition-colors">
                <Mail className="w-4 h-4" aria-hidden="true" />
                {companyInfo.email}
              </a>
            </div>
          </div>

          {/* Right Side - Services (Appears second on mobile) */}
          <div className="order-2 lg:order-2 space-y-6">
            {/* Hero Text */}
            <div className="text-center lg:text-left mb-8">
              <div className="inline-block mb-4 px-4 py-1.5 bg-amber-500/10 border border-amber-500/30 rounded-full">
                <p className="text-amber-400 text-sm font-semibold">
                  SEBI-Registered Research Analyst with 12+ years of proven experience
                </p>
              </div>
              <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-4 leading-tight">
                Make Smarter <span className="text-amber-400">Investment</span> Decisions
              </h1>
              <p className="text-gray-400 text-lg">
                Join 500+ investors who trust Equi Pulse for data-driven market insights and proven trading strategies.
              </p>
            </div>

            {/* Services */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-emerald-400 mb-4 flex items-center gap-2">
                <TrendingUp className="w-5 h-5" aria-hidden="true" />
                Our Research Services
              </h3>
              
              {services.map((service, index) => (
                <div
                  key={index}
                  className={`relative p-5 bg-gradient-to-br from-slate-900/80 to-slate-800/80 rounded-xl border ${
                    service.popular ? 'border-amber-500/50' : 'border-emerald-700/30'
                  } hover:border-amber-500/50 transition-all duration-300`}
                >
                  {service.popular && (
                    <div className="absolute -top-3 right-4 px-3 py-1 bg-gradient-to-r from-amber-600 to-amber-500 text-white text-xs font-bold rounded-full">
                      Most Popular
                    </div>
                  )}
                  
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <h4 className="text-lg font-bold text-white">{service.name}</h4>
                      <p className="text-amber-400 font-semibold">{service.price}<span className="text-gray-400 text-sm">/month</span></p>
                    </div>
                  </div>
                  
                  <ul className="space-y-2">
                    {service.features.map((feature, idx) => (
                      <li key={idx} className="flex items-start gap-2 text-sm text-gray-300">
                        <CheckCircle className="w-4 h-4 text-emerald-500 mt-0.5 flex-shrink-0" aria-hidden="true" />
                        {feature}
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>

            {/* Why Choose Us */}
            <div className="mt-8 p-5 bg-emerald-900/20 rounded-xl border border-emerald-700/30">
              <h4 className="text-lg font-semibold text-white mb-4">Why Choose Equi Pulse?</h4>
              <div className="grid grid-cols-2 gap-4">
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 bg-emerald-600/20 rounded-lg flex items-center justify-center">
                    <Shield className="w-4 h-4 text-emerald-500" aria-hidden="true" />
                  </div>
                  <span className="text-sm text-gray-300">SEBI Registered</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 bg-amber-600/20 rounded-lg flex items-center justify-center">
                    <TrendingUp className="w-4 h-4 text-amber-500" aria-hidden="true" />
                  </div>
                  <span className="text-sm text-gray-300">Proven Track Record</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 bg-emerald-600/20 rounded-lg flex items-center justify-center">
                    <Users className="w-4 h-4 text-emerald-500" aria-hidden="true" />
                  </div>
                  <span className="text-sm text-gray-300">500+ Happy Clients</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 bg-amber-600/20 rounded-lg flex items-center justify-center">
                    <Send className="w-4 h-4 text-amber-500" aria-hidden="true" />
                  </div>
                  <span className="text-sm text-gray-300">Instant Alerts</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>

      {/* BSE Disclaimer Footer */}
      <footer className="bg-slate-950 border-t border-amber-500/20 mt-12" role="contentinfo">
        <div className="container mx-auto px-4 py-8">
          {/* Company Info */}
          <div className="text-center mb-6">
            <p className="text-amber-400 font-semibold mb-1">{companyInfo.name}</p>
            <p className="text-gray-400 text-sm">SEBI Registration No: {companyInfo.sebiReg} | GST: {companyInfo.gstNo}</p>
          </div>

          {/* BSE Disclaimer */}
          <div className="max-w-4xl mx-auto">
            <div className="p-4 bg-slate-900/50 rounded-lg border border-red-900/30">
              <h4 className="text-red-400 font-semibold text-sm mb-2 text-center">IMPORTANT DISCLAIMER</h4>
              <p className="text-gray-400 text-xs leading-relaxed text-center">
                <strong className="text-gray-300">INVESTMENTS IN THE SECURITIES MARKET ARE SUBJECT TO MARKET RISK. READ ALL RELATED DOCUMENTS CAREFULLY BEFORE INVESTING.</strong>
              </p>
              <p className="text-gray-400 text-xs leading-relaxed mt-3 text-center">
                Registration granted by SEBI and certification from NISM in no way guarantee performance of the intermediary or provide any assurance of returns to investors. Past performance is not indicative of future results. All investment decisions are at the sole discretion of the investor. Equi Pulse Research does not guarantee any returns or profits. The information provided is for educational purposes only and should not be construed as personalized investment advice.
              </p>
            </div>
          </div>

          {/* Copyright */}
          <div className="text-center mt-6 pt-6 border-t border-emerald-700/20">
            <p className="text-gray-400 text-xs">
              © {new Date().getFullYear()} {companyInfo.name} | All rights reserved
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
